import { createSelector } from '@reduxjs/toolkit';
import { selectActiveCommunityId } from 'community/state/selectors';
import { UserPrivilegeName } from 'domain/UserProfile';
import { find, some } from 'lodash-es';
import prop from 'lodash/fp/prop';
import type { RootState } from 'store';
import { tagServiceApi } from 'tags/tagServiceApi';
import { selectUserAccountId } from 'user/api/selectors';

import { communityApi } from './communityApi';

const selectRootState = (state: RootState) => state;

// My Community
export const selectMyCommunities = communityApi.endpoints.getMyCommunities.select();

// The user may be viewing a community page they don’t belong to yet
// (e.g., Register Natter Event page or Open Natter Availability Submission page).
// In that case, the active community id could be outside the list of their communities,
// and selectMyCommunity should return "undefined".
export const selectMyCommunity = createSelector(
  [selectActiveCommunityId, selectMyCommunities],
  (activeCommunityId, myCommunitiesDataResult) =>
    activeCommunityId ? find(myCommunitiesDataResult?.data?.myCommunities, { id: activeCommunityId }) : undefined
);

export const selectMyCommunityId = createSelector(selectMyCommunity, prop('id'));
export const selectMyCommunityHash = createSelector(selectMyCommunity, prop('hash'));
export const selectMyCommunityName = createSelector(selectMyCommunity, prop('name'));
export const selectCommunitySettings = createSelector(selectMyCommunity, prop('communitySettings'));
export const selectMyCommunityOwnerId = createSelector(selectMyCommunity, prop('ownerId'));
export const selectMyCommunityAutomaticOptOutEnabled = createSelector(
  selectCommunitySettings,
  prop('automaticOptOutEnabled')
);

export const selectIsOpenNattersEnabledForMyCommunity = createSelector(
  selectCommunitySettings,
  prop('openNatterEnabled')
);

export const selectIsNatalieEventsEnabledForMyCommunity = createSelector(
  selectCommunitySettings,
  prop('natalieEnabled')
);

export const selectIsEventTagsEnabledForMyCommunity = createSelector(selectCommunitySettings, prop('eventTagsEnabled'));

export const selectHasNoCommunity = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.data?.hasNoCommunity
);
export const selectMyCommunityIsLoading = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.isLoading
);
export const selectMyCommunityIsError = createSelector(
  selectMyCommunities,
  (myCommunityDataResult) => !!myCommunityDataResult?.isError
);
export const selectMyCommunitiesIds = createSelector(selectMyCommunities, (myCommunityDataResult) =>
  myCommunityDataResult?.data?.myCommunities.map((c) => c.id)
);
export const selectIsPartOfCommunity = (communityId: number | undefined) =>
  createSelector(selectMyCommunitiesIds, (myCommunities) =>
    communityId ? myCommunities?.includes(communityId) : false
  );

// Community Member
export const selectCommunityMemberByCommunityId = (state: RootState, id: number | undefined) =>
  communityApi.endpoints.getCurrentCommunityMember.select({ communityId: id })(state)?.data;

export const selectCurrentCommunityMember = createSelector(selectRootState, selectMyCommunityId, (state, id) =>
  selectCommunityMemberByCommunityId(state, id)
);

export const selectHasPrivilege = (privilegeName: UserPrivilegeName) =>
  createSelector(selectCurrentCommunityMember, (member) => some(member?.privileges, { name: privilegeName }));

export const selectCurrentCommunityMemberIsLoading = createSelector(
  selectRootState,
  selectMyCommunityId,
  (state, id) => communityApi.endpoints.getCurrentCommunityMember.select({ communityId: id })(state).isLoading
);

export const selectCurrentCommunityMemberSocials = createSelector(
  selectRootState,
  selectMyCommunityId,
  (state, id) => selectCommunityMemberByCommunityId(state, id)?.socials ?? {}
);

const selectTagCategoriesForMyCommunity = createSelector(selectRootState, selectMyCommunityId, (state, communityId) =>
  communityId ? tagServiceApi.endpoints.getTagCategories.select({ communityId })(state)?.data : undefined
);

const selectMyTagsForMyCommunity = createSelector(
  selectRootState,
  selectMyCommunityId,
  (state) => selectUserAccountId(state),
  (state, communityId, accountId) =>
    communityId && accountId
      ? tagServiceApi.endpoints.getMyTags.select({ communityId, accountId })(state)?.data
      : undefined
);

const selectIsTagDataReady = createSelector(
  selectRootState,
  selectMyCommunityId,
  (state) => selectUserAccountId(state),
  (state, communityId, accountId) => {
    if (!communityId) return true; // No community — nothing to load
    const categories = tagServiceApi.endpoints.getTagCategories.select({ communityId })(state);
    const myTags = accountId ? tagServiceApi.endpoints.getMyTags.select({ communityId, accountId })(state) : undefined;
    const isLoading = categories?.isLoading || myTags?.isLoading;
    const isUninitialized = categories?.isUninitialized || myTags?.isUninitialized;
    return !isLoading && !isUninitialized;
  }
);

export const selectCommunityMemberConfigurationInfo = createSelector(
  selectMyCommunity,
  selectCurrentCommunityMember,
  selectCurrentCommunityMemberIsLoading,
  selectTagCategoriesForMyCommunity,
  selectMyTagsForMyCommunity,
  selectIsTagDataReady,
  (myCommunity, communityMember, isLoading, tagCategories, myTags, isTagDataReady) => {
    const isRegistered = !!communityMember;
    const roleFieldDisabled = myCommunity?.communitySettings?.roleFieldDisabled;
    const eventTagsEnabled = myCommunity?.communitySettings?.eventTagsEnabled;
    const isRoleConfigured = !!communityMember?.role;

    // When eventTagsEnabled is on, tags are managed at event level — skip community tag checks.
    // Until tag data is ready, we don't know the true configuration state.
    // Return isLoading: true so consumers wait before making decisions.
    const isTagsConfigured = eventTagsEnabled
      ? true
      : !isTagDataReady
        ? true
        : tagCategories === undefined ||
          tagCategories.length === 0 ||
          tagCategories.every((c) => !c.isSingleSelection || (myTags?.[c.name]?.length ?? 0) > 0);

    const isAccountConfigured = !!(
      communityMember?.displayName &&
      (roleFieldDisabled || isRoleConfigured) &&
      isTagsConfigured
    );

    return {
      roleFieldDisabled,
      isRoleConfigured,
      isTagsConfigured,
      isAccountConfigured,
      isRegistered,
      isLoading: isLoading || (!eventTagsEnabled && !isTagDataReady),
    };
  }
);

export const selectCommunityBySlug = (slug: string | undefined | null) =>
  createSelector(selectMyCommunities, (myCommunitiesDataResult) =>
    slug ? find(myCommunitiesDataResult?.data?.myCommunities, { slug }) : undefined
  );
